import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useAddDieta } from "@/hooks/useHospitalization";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const TIPO_DIETA = ["alimentacao", "agua", "fluidoterapia"] as const;
type TipoDieta = (typeof TIPO_DIETA)[number];

const schema = z.object({
  tipo: z.enum(TIPO_DIETA),
  descricao: z.string().optional(),
  quantidade_ml: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface DietaItem {
  id: string;
  registrado_at: string;
  tipo: string;
  descricao: string;
  quantidade_ml?: number | null;
}

interface DietaTabProps {
  hospitalizationId: string;
  customerId: string;
  dieta: DietaItem[];
}

export function DietaTab({ hospitalizationId, customerId, dieta }: DietaTabProps) {
  const { t, i18n } = useTranslation("hospitalization");
  const [showForm, setShowForm] = useState(false);
  const addMutation = useAddDieta();
  const locale = i18n.language === "pt" ? ptBR : undefined;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tipo: "alimentacao", descricao: "", quantidade_ml: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await addMutation.mutateAsync({
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
        tipo: values.tipo,
        descricao: values.descricao,
        quantidade_ml: values.quantidade_ml ? parseFloat(values.quantidade_ml) : null,
      });
      toast.success(t("success.dietaSaved"));
      form.reset();
      setShowForm(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("dieta.novoRegistro")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <FormField
                  control={form.control}
                  name="tipo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("dieta.tipo")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIPO_DIETA.map((tp) => (
                            <SelectItem key={tp} value={tp}>
                              {t(`dieta.tipoOpcoes.${tp}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("dieta.descricao")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="quantidade_ml"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("dieta.quantidade")}</FormLabel>
                      <FormControl>
                        <Input type="number" step="any" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    {t("dieta.cancelar")}
                  </Button>
                  <Button type="submit" disabled={addMutation.isPending}>
                    {t("dieta.salvar")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {dieta.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t("dieta.semRegistros")}
        </p>
      )}

      {[...dieta]
        .sort(
          (a, b) =>
            new Date(b.registrado_at).getTime() - new Date(a.registrado_at).getTime()
        )
        .map((d) => (
          <Card key={d.id}>
            <CardContent className="pt-3 pb-3 flex items-start gap-3">
              <Badge variant="outline">
                {t(`dieta.tipoOpcoes.${d.tipo as TipoDieta}`)}
              </Badge>
              <div className="flex-1">
                <p className="text-sm">{d.descricao}</p>
                {d.quantidade_ml != null && (
                  <p className="text-xs text-muted-foreground">{d.quantidade_ml}ml</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {format(new Date(d.registrado_at), "dd/MM HH:mm", { locale })}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
